import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Radar, Loader2, RefreshCw, X, Ban, Mail } from "lucide-react";
import { toast } from "sonner";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { API } from "../../lib/api";

const client = axios.create({ baseURL: API, timeout: 30000, withCredentials: true });

const KINDS = {
  scan: { label: "Hidden-file scans", color: "#f87171" },
  throttle: { label: "Guess limits hit", color: "#fbbf24" },
  chat_extraction: { label: "Chat extraction blocked", color: "#38bdf8" },
  architect_denied: { label: "Architect-only denied", color: "#a1a1aa" },
  blocked: { label: "Blocked address hits", color: "#e879f9" },
};
const errMsg = (e, fb) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : fb);
const RANGES = [7, 30, 90, 3650];
const rangeLabel = (d) => (d === 3650 ? "All" : `${d}d`);
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

function Totals({ data }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-zinc-800 border border-zinc-800" data-testid="probe-totals">
      {Object.entries(KINDS).map(([k, v]) => (
        <div key={k} className="bg-[#0b0b0d] p-4" data-testid={`probe-total-${k}`}>
          <div className="text-[10px] uppercase tracking-[0.25em]" style={{ color: v.color }}>{v.label}</div>
          <div className="text-2xl text-zinc-100 font-mono mt-1">{data.totals[k]}</div>
        </div>
      ))}
    </div>
  );
}

function DailyChart({ daily }) {
  const rows = daily.length > 120 ? daily.filter((d) => Object.keys(KINDS).some((k) => d[k] > 0)) : daily;
  return (
    <div className="h-56 border border-zinc-800 bg-[#0b0b0d] p-3" data-testid="probe-daily-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows.map((d) => ({ ...d, label: d.day.slice(5) }))}>
          <CartesianGrid stroke="#27272a" vertical={false} />
          <XAxis dataKey="label" tick={{ fill: "#71717a", fontSize: 10 }} />
          <YAxis allowDecimals={false} tick={{ fill: "#71717a", fontSize: 10 }} width={28} />
          <Tooltip contentStyle={{ background: "#09090b", border: "1px solid #3f3f46", fontSize: 11 }} />
          <Legend wrapperStyle={{ fontSize: 10 }} />
          {Object.entries(KINDS).map(([k, v]) => (
            <Bar key={k} dataKey={k} name={v.label} stackId="a" fill={v.color} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function TopIps({ rows, onPick, blocked, onBlock }) {
  return (
    <div className="border border-zinc-800 bg-[#0b0b0d]" data-testid="probe-top-ips">
      <div className="px-4 py-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 border-b border-zinc-800">Top visitor addresses</div>
      {rows.length === 0 && <p className="p-4 text-xs text-zinc-500">Nothing recorded in this range.</p>}
      {rows.map((r) => (
        <div key={r.ip} role="button" tabIndex={0} onClick={() => onPick(r.ip)} data-testid={`probe-ip-row-${r.ip}`}
          onKeyDown={(e) => e.key === "Enter" && onPick(r.ip)}
          className="w-full text-left px-4 py-2 border-b border-zinc-900 hover:bg-zinc-900/60 transition-colors cursor-pointer">
          <div className="flex items-center gap-3 text-xs">
            <span className="font-mono text-zinc-100">{r.ip}</span>
            {blocked.has(r.ip) ? (
              <span className="text-[10px] uppercase tracking-[0.2em] text-fuchsia-300" data-testid={`probe-ip-blocked-${r.ip}`}>blocked</span>
            ) : (
              <button onClick={(e) => { e.stopPropagation(); onBlock(r.ip); }} data-testid={`probe-block-${r.ip}`}
                className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 border border-zinc-700 text-zinc-300 hover:border-red-500/60 hover:text-red-300 inline-flex items-center gap-1 transition-colors">
                <Ban className="h-3 w-3" /> Block
              </button>
            )}
            <span className="text-zinc-400">{r.count}×</span>
            <span className="flex gap-1">
              {r.kinds.map((k) => <span key={k} className="h-2 w-2 rounded-full" style={{ background: KINDS[k]?.color }} title={KINDS[k]?.label} />)}
            </span>
            <span className="ml-auto text-[10px] text-zinc-600">last {fmt(r.last_seen)}</span>
          </div>
          <div className="text-[10px] text-zinc-600 truncate mt-0.5">{r.ua || "no browser info"}</div>
        </div>
      ))}
    </div>
  );
}

function RecentEvents({ rows, kind, setKind, ip, clearIp }) {
  return (
    <div className="border border-zinc-800 bg-[#0b0b0d]" data-testid="probe-recent">
      <div className="px-4 py-2 border-b border-zinc-800 flex flex-wrap items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mr-2">Recent events</span>
        {["", ...Object.keys(KINDS)].map((k) => (
          <button key={k || "all"} onClick={() => setKind(k)} data-testid={`probe-filter-${k || "all"}`}
            className={`text-[11px] px-2.5 py-1 border transition-colors ${kind === k ? "border-amber-500/60 text-amber-300" : "border-zinc-700 text-zinc-300 hover:text-zinc-100"}`}>
            {k ? KINDS[k].label : "All"}
          </button>
        ))}
        {ip && (
          <button onClick={clearIp} data-testid="probe-ip-filter-clear"
            className="text-[10px] px-2 py-0.5 border border-sky-500/60 text-sky-300 inline-flex items-center gap-1">
            {ip} <X className="h-3 w-3" />
          </button>
        )}
      </div>
      <div className="max-h-80 overflow-y-auto">
        {rows.length === 0 && <p className="p-4 text-xs text-zinc-500" data-testid="probe-recent-empty">No events match.</p>}
        {rows.map((e) => (
          <div key={e.event_id} className="px-4 py-2 border-b border-zinc-900 text-xs flex gap-3 items-start" data-testid={`probe-event-${e.event_id}`}>
            <span className="h-2 w-2 mt-1.5 rounded-full shrink-0" style={{ background: KINDS[e.kind]?.color }} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap gap-x-3">
                <span className="text-zinc-200">{KINDS[e.kind]?.label}</span>
                <span className="font-mono text-zinc-400">{e.method} {e.path}</span>
                <span className="font-mono text-sky-300">{e.ip}</span>
              </div>
              <div className="text-[10px] text-zinc-600 truncate">{e.detail}{e.ua ? ` · ${e.ua}` : ""}</div>
            </div>
            <span className="text-[10px] text-zinc-600 shrink-0">{fmt(e.at)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BlockedList({ rows, onUnblock }) {
  return (
    <div className="border border-zinc-800 bg-[#0b0b0d]" data-testid="probe-blocked-list">
      <div className="px-4 py-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 border-b border-zinc-800">
        Blocked addresses ({rows.length})
      </div>
      {rows.length === 0 && <p className="p-4 text-xs text-zinc-500" data-testid="probe-blocked-empty">No addresses blocked.</p>}
      {rows.map((b) => (
        <div key={b.ip} className="px-4 py-2 border-b border-zinc-900 flex items-center gap-3 text-xs" data-testid={`probe-blocked-row-${b.ip}`}>
          <span className="font-mono text-fuchsia-300">{b.ip}</span>
          <span className="text-zinc-500 truncate">{b.reason || "no reason given"}</span>
          <span className="ml-auto text-[10px] text-zinc-600">{fmt(b.blocked_at)}</span>
          <button onClick={() => onUnblock(b.ip)} data-testid={`probe-unblock-${b.ip}`}
            className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 border border-zinc-700 text-zinc-300 hover:border-emerald-500/60 hover:text-emerald-300 transition-colors">
            Unblock
          </button>
        </div>
      ))}
    </div>
  );
}

function DigestCard() {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => client.get("/architect/security/digest").then(({ data }) => setInfo(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const sendTest = async () => {
    setBusy(true);
    try {
      await client.post("/architect/security/digest/send-test");
      toast.success("Test digest sent. Check your inbox");
    } catch (e) {
      toast.error(errMsg(e, "Couldn't send the digest."));
    } finally {
      setBusy(false);
    }
  };

  if (!info) return null;
  const last = info.runs?.[0];
  return (
    <div className="border border-zinc-800 bg-[#0b0b0d] p-4 flex flex-wrap items-center gap-4 text-xs" data-testid="probe-digest-card">
      <Mail className="h-4 w-4 text-amber-300" />
      <div className="flex-1 min-w-[200px]">
        <div className="text-zinc-200">Weekly email digest · {info.schedule}</div>
        <div className="text-[11px] text-zinc-500 mt-0.5" data-testid="probe-digest-status">
          {info.configured ? "Gmail connected" : "Gmail not connected yet"}
          {last ? ` · last run ${last.week_key}: ${last.status}${last.error ? ` (${last.error})` : ""}` : " · no runs yet"}
        </div>
      </div>
      <button onClick={sendTest} disabled={busy || !info.configured} data-testid="probe-digest-send-test"
        className="px-3 py-2 border border-amber-500/60 text-amber-300 hover:bg-amber-500 hover:text-zinc-950 text-[11px] uppercase tracking-[0.2em] disabled:opacity-40 transition-colors">
        {busy ? "Sending…" : "Send test now"}
      </button>
    </div>
  );
}

export const ProbeDashboard = () => {
  const [days, setDays] = useState(30);
  const [kind, setKind] = useState("");
  const [ip, setIp] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState([]);

  const loadBlocked = () => client.get("/architect/security/blocked").then(({ data: d }) => setBlocked(d.blocked)).catch(() => {});
  useEffect(() => { loadBlocked(); }, []);

  useEffect(() => {
    if (data && window.location.hash === "#probe-dashboard") {
      document.getElementById("probe-dashboard")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [data]);

  const block = async (ipToBlock) => {
    const reason = window.prompt(`Block ${ipToBlock}? Optional note:`, "repeat probing");
    if (reason === null) return;
    try {
      await client.post("/architect/security/block", { ip: ipToBlock, reason });
      toast.success(`${ipToBlock} blocked`);
      loadBlocked();
    } catch (e) {
      toast.error(errMsg(e, "Couldn't block that address."));
    }
  };

  const unblock = async (ipToUnblock) => {
    try {
      await client.delete(`/architect/security/block/${encodeURIComponent(ipToUnblock)}`);
      toast.success(`${ipToUnblock} unblocked`);
      loadBlocked();
    } catch (e) {
      toast.error(errMsg(e, "Couldn't unblock that address."));
    }
  };

  const load = useCallback(() => {
    setBusy(true);
    setError(null);
    client.get("/architect/security/probe-stats", { params: { days, kind: kind || undefined, ip: ip || undefined } })
      .then(({ data: d }) => setData(d))
      .catch((e) => setError(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Couldn't load the dashboard."))
      .finally(() => setBusy(false));
  }, [days, kind, ip]);

  useEffect(() => { load(); }, [load]);

  return (
    <section id="probe-dashboard" data-testid="settings-probe-dashboard" className="mt-16 border-t border-zinc-800 pt-10 scroll-mt-24">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-amber-300 mb-1 inline-flex items-center gap-2">
            <Radar className="h-3 w-3" /> probe dashboard · architect only
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl tracking-[0.1em] text-zinc-100">Blocked activity</h2>
          <p className="text-zinc-400 text-sm mt-2 max-w-2xl leading-relaxed">
            Every scan, guess-limit hit, blocked extraction attempt and denied admin request, by day. Click an address to see only its events.
          </p>
        </div>
        <div className="flex items-center gap-1">
          {RANGES.map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`probe-range-${rangeLabel(d)}`}
              className={`text-[11px] uppercase tracking-[0.2em] px-3 py-2 border transition-colors ${days === d ? "border-amber-500/60 text-amber-300" : "border-zinc-700 text-zinc-300 hover:text-zinc-100"}`}>
              {rangeLabel(d)}
            </button>
          ))}
          <button onClick={load} data-testid="probe-refresh" aria-label="Refresh"
            className="p-2 text-zinc-500 hover:text-zinc-200 transition-colors">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>
      {error && <p className="text-xs text-red-300 mb-3" data-testid="probe-error">{error}</p>}
      {!data ? (
        !error && <Loader2 className="h-4 w-4 animate-spin text-zinc-500" data-testid="probe-loading" />
      ) : (
        <div className="space-y-4">
          <Totals data={data} />
          <DailyChart daily={data.daily} />
          <div className="grid lg:grid-cols-2 gap-4">
            <TopIps rows={data.top_ips} onPick={setIp} blocked={new Set(blocked.map((b) => b.ip))} onBlock={block} />
            <RecentEvents rows={data.recent} kind={kind} setKind={setKind} ip={ip} clearIp={() => setIp("")} />
          </div>
          <BlockedList rows={blocked} onUnblock={unblock} />
          <DigestCard />
          <p className="text-[10px] text-zinc-600" data-testid="probe-all-time">
            {data.all_time_total} events recorded all-time · kept forever
          </p>
        </div>
      )}
    </section>
  );
};
